import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IonBackButton, IonButtons, IonContent, IonHeader, IonPage, IonTitle, IonToolbar, IonSpinner, IonRefresher, IonRefresherContent, RefresherEventDetail, IonIcon, IonButton } from '@ionic/react';
import { useParams } from 'react-router';
import { api } from '@/utils/api';
import { useTranslation } from 'react-i18next';
import { Post } from '@/components/Post';
import { useUserProfile } from '@/context/UserProfileContext';
import { useToast } from '@/hooks/use-toast';
import { refreshOutline } from 'ionicons/icons';
import * as Sentry from '@sentry/react';

interface TopicParams {
  topicName: string;
}

const Topic: React.FC = () => {
  const { topicName } = useParams<TopicParams>();
  const { t } = useTranslation();
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [hashtagDetails, setHashtagDetails] = useState<{ name: string, count: number } | null>(null);
  const pageRef = useRef<HTMLElement>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  const { toast } = useToast();
  const { profile } = useUserProfile();

  // Fetch hashtag details
  const fetchHashtagDetails = useCallback(async () => {
    try {
      const { data } = await api.get(`/hashtags/${encodeURIComponent(topicName)}`);
      setHashtagDetails(data);
    } catch (error) {
      console.error('Error fetching hashtag details:', error);
    }
  }, [topicName]);

  // Fetch posts with hashtag filter
  const fetchPosts = useCallback(
    async (isInitial: boolean = false) => {
      if (isInitial) {
        setLoading(true);
        setPage(1);
        // Fetch hashtag details when initially loading posts
        fetchHashtagDetails();
      } else {
        setLoadingMore(true);
      }

      try {
        let apiUrl = `/posts?page=${isInitial ? 1 : page}&limit=10&hashtag=${encodeURIComponent(topicName)}`;

        // Always include profileId for like status
        if (profile?.id) {
          apiUrl += `&profileId=${profile.id}`;
        }

        const { data: { posts: newPosts, metadata } } = await api.get(apiUrl);

        if (isInitial) {
          setPosts(newPosts);
        } else {
          setPosts(prevPosts => [...prevPosts, ...newPosts]);
        }

        // We don't need to set post count here as we get it from hashtag details
        setHasMore(page < metadata.totalPages);
        if (!isInitial) {
          setPage(prevPage => prevPage + 1);
        }
      } catch (error) {
        console.error('Error fetching hashtag posts:', error);
        Sentry.captureException(error);
        setFetchError(true);
        toast({
          description: t('cant_fetch_posts'),
          variant: 'destructive'
        });
      } finally {
        if (isInitial) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    },
    [page, profile, topicName, toast, t],
  );

  // Fetch only new posts
  const fetchNewPosts = useCallback(async () => {
    if (posts.length === 0) {
      return;
    }

    const latestPostTimestamp = posts[0]?.createdAt; // Get the timestamp of the most recent post

    try {
      let apiUrl = `/posts?since=${latestPostTimestamp}&hashtag=${encodeURIComponent(topicName)}`;

      // Always include profileId for like status
      if (profile?.id) {
        apiUrl += `&profileId=${profile.id}`;
      }

      const { data: { posts: newPosts } } = await api.get(apiUrl);

      if (newPosts && newPosts.length > 0) {
        setPosts(prevPosts => {
          // Create a Set of existing post IDs for fast lookup
          const existingPostIds = new Set(prevPosts.map((post: any) => post.id));

          // Filter out duplicates from newPosts
          const uniqueNewPosts = newPosts.filter((post: any) => !existingPostIds.has(post.id));

          // Merge unique new posts with existing posts
          return [...uniqueNewPosts, ...prevPosts];
        });
      }
      else {
        toast({
          description: t('no_new_posts'),
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('Error fetching new posts:', error);
    }
  }, [posts, profile, topicName]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    setFetchError(false);
    await fetchNewPosts();
    event.detail.complete();
  };

  // Function to retry fetching posts
  const retryFetchPosts = () => {
    setFetchError(false);
    fetchPosts(true);
  };

  const loadMorePosts = useCallback(() => {
    if (!loadingMore && hasMore && profile) {
      fetchPosts(false);
    }
  }, [loadingMore, hasMore, fetchPosts, profile]);

  const handleDeletePost = useCallback(
    async (postId: number) => {
      try {
        const response = await api.delete(`/posts/${postId}`);

        if (!response.data) {
          throw new Error('Failed to delete post');
        }

        setPosts(prevPosts => prevPosts.filter(post => post.id !== postId));

        toast({
          description: t('post_deleted'),
        });

        return response.data;
      } catch (error) {
        console.error('Error deleting post:', error);
      }
    },
    [toast, t],
  );

  // Set up intersection observer for infinite scrolling
  const lastPostElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (loading || loadingMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          loadMorePosts();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, loadingMore, hasMore, loadMorePosts],
  );

  // Initial fetch
  useEffect(() => {
    fetchPosts(true);
  }, [topicName]);

  return (
    <IonPage ref={pageRef}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/discover" />
          </IonButtons>
          <IonTitle className="text-center">
            #{topicName}
            {hashtagDetails && (
              <div className="text-xs text-gray-500">
                {hashtagDetails.count} {hashtagDetails.count === 1 ? t('post') : t('posts')}
              </div>
            )}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <div className="mx-auto max-w-lg space-y-4 px-1">
          {loading ? (
            <div className="flex justify-center py-8">
              <IonSpinner name="crescent" />
            </div>
          ) : fetchError ? (
            <div className="flex flex-col items-center justify-center gap-4 py-8 px-4 text-center">
              <IonButton
                color="medium"
                fill="outline"
                shape="round"
                onClick={retryFetchPosts}
              >
                <IonIcon slot="icon-only" icon={refreshOutline} />
              </IonButton>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{t('cant_fetch_posts')}</h3>
            </div>
          ) : posts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              <h3 className="text-lg font-medium mb-2">{t('no_posts_yet')}</h3>
              <p className="text-muted-foreground mb-4">
                {t('be_first_to_post_with_hashtag', { hashtag: topicName })}
              </p>
            </div>
          ) : (
            <div className="space-y-4 sm:px-0">
              {posts.map((post, index) => (
                <Post
                  key={post.id}
                  ref={index === posts.length - 2 ? lastPostElementRef : null}
                  id={post.id}
                  user={post.user}
                  media={post.media}
                  likes={post.likes}
                  isLiked={post.isLiked}
                  course={post.course}
                  caption={post.caption}
                  comments={post.comments}
                  audience={post.audience}
                  profileId={profile!.id}
                  createdAt={post.createdAt}
                  onDelete={handleDeletePost}
                  pageRef={pageRef}
                />
              ))}
              {loadingMore && (
                <div className="flex justify-center py-4">
                  <IonSpinner name="crescent" />
                </div>
              )}
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Topic;
